import { useRef, useEffect, useCallback } from 'react';

const ACTION_BTN_WIDTH = 72; // px per action button
const OPEN_THRESHOLD = 50;   // px of travel to snap open

let _idCounter = 0;

/**
 * SwipeableRow — wraps any list item with swipe-left-to-reveal actions.
 *
 * Props:
 *   actions  — array of { icon, label, color, onAction }
 *   disabled — pass true to disable entirely (renders children as-is)
 *
 * Usage:
 *   <SwipeableRow actions={[
 *     { icon: '🗑', label: 'Delete', color: '#e03131', onAction: () => handleDelete(id) },
 *     { icon: '📌', label: 'Pin',    color: '#f59f00', onAction: () => handlePin(id) },
 *   ]}>
 *     <YourCardComponent />
 *   </SwipeableRow>
 *
 * Notes:
 *   - Transform is applied directly to the DOM (no state re-renders during drag)
 *   - Uses non-passive touchmove to block scroll during horizontal swipe
 *   - Dispatches 'swiperow:open' custom event so other open rows auto-close
 */
export default function SwipeableRow({ children, actions = [], disabled = false }) {
  const rowId = useRef(++_idCounter).current;
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const touchState = useRef(null);  // { startX, startY, startOffset, decided }
  const isOpenRef = useRef(false);

  const totalWidth = actions.length * ACTION_BTN_WIDTH;

  // Direct DOM transform — avoids re-renders during drag
  const applyTransform = useCallback((px, animate) => {
    const el = contentRef.current;
    if (!el) return;
    el.style.transition = animate ? 'transform 0.2s ease' : 'none';
    el.style.transform = `translateX(${px}px)`;
  }, []);

  const snapClose = useCallback(() => {
    applyTransform(0, true);
    isOpenRef.current = false;
  }, [applyTransform]);

  const snapOpen = useCallback(() => {
    applyTransform(-totalWidth, true);
    isOpenRef.current = true;
    document.dispatchEvent(new CustomEvent('swiperow:open', { detail: { id: rowId } }));
  }, [applyTransform, totalWidth, rowId]);

  // Close when another row opens
  useEffect(() => {
    const handler = (e) => {
      if (e.detail.id !== rowId && isOpenRef.current) snapClose();
    };
    document.addEventListener('swiperow:open', handler);
    return () => document.removeEventListener('swiperow:open', handler);
  }, [rowId, snapClose]);

  // Close when tapping outside this row
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target) && isOpenRef.current) {
        snapClose();
      }
    };
    document.addEventListener('touchstart', handler, { passive: true });
    document.addEventListener('mousedown', handler);
    return () => {
      document.removeEventListener('touchstart', handler);
      document.removeEventListener('mousedown', handler);
    };
  }, [snapClose]);

  // Trackpad two-finger horizontal swipe (wheel events with deltaX)
  useEffect(() => {
    const el = contentRef.current;
    if (!el || disabled || actions.length === 0) return;

    let gestureOffset = null; // null = gesture not yet started
    let endTimer = null;

    const finish = () => {
      endTimer = null;
      if (gestureOffset === null) return;
      if (gestureOffset < -OPEN_THRESHOLD) {
        snapOpen();
      } else {
        snapClose();
      }
      gestureOffset = null;
    };

    const onWheel = (e) => {
      // Let vertical scrolling pass through untouched
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) return;

      // Claim every horizontal wheel event immediately — this is what stops the
      // browser from interpreting any right-swipe as a back-navigation gesture.
      e.preventDefault();

      if (Math.abs(e.deltaX) < 2) return;

      // Seed from current DOM transform on first tick of a gesture
      if (gestureOffset === null) {
        const current = parseFloat(el.style.transform?.replace('translateX(', '') || '0');
        gestureOffset = isNaN(current) ? 0 : current;
      }

      gestureOffset = Math.max(-totalWidth, Math.min(0, gestureOffset - e.deltaX));
      applyTransform(gestureOffset, false);

      if (endTimer) clearTimeout(endTimer);
      endTimer = setTimeout(finish, 150);
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      el.removeEventListener('wheel', onWheel);
      if (endTimer) clearTimeout(endTimer);
    };
  }, [disabled, actions.length, totalWidth, applyTransform, snapOpen, snapClose]);

  // Non-passive touchmove — must be attached via addEventListener to call preventDefault
  useEffect(() => {
    const el = contentRef.current;
    if (!el || disabled || actions.length === 0) return;

    const onMove = (e) => {
      const ts = touchState.current;
      if (!ts) return;

      const touch = e.touches[0];
      const dx = touch.clientX - ts.startX;
      const dy = touch.clientY - ts.startY;

      // Decide direction on first meaningful movement
      if (!ts.decided) {
        if (Math.abs(dx) < 4 && Math.abs(dy) < 4) return;
        ts.decided = Math.abs(dx) >= Math.abs(dy) ? 'h' : 'v';
      }

      if (ts.decided === 'v') return; // let browser scroll

      e.preventDefault();

      const newOffset = Math.max(-totalWidth, Math.min(0, ts.startOffset + dx));
      applyTransform(newOffset, false);
    };

    el.addEventListener('touchmove', onMove, { passive: false });
    return () => el.removeEventListener('touchmove', onMove);
  }, [disabled, actions.length, totalWidth, applyTransform]);

  const handleTouchStart = useCallback((e) => {
    if (disabled || actions.length === 0) return;
    const touch = e.touches[0];
    touchState.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      startOffset: isOpenRef.current ? -totalWidth : 0,
      decided: null,
    };
  }, [disabled, actions.length, totalWidth]);

  const handleTouchEnd = useCallback(() => {
    const ts = touchState.current;
    touchState.current = null;
    if (!ts || ts.decided !== 'h') return;

    // Read current transform to decide snap direction
    const el = contentRef.current;
    const current = el
      ? parseFloat(el.style.transform?.replace('translateX(', '') || '0')
      : 0;

    if (current < -OPEN_THRESHOLD) {
      snapOpen();
    } else {
      snapClose();
    }
  }, [snapOpen, snapClose]);

  if (disabled || actions.length === 0) return children;

  return (
    <div ref={containerRef} style={{ position: 'relative', overflow: 'hidden', isolation: 'isolate' }}>
      {/* Action buttons — sit behind, revealed as content slides left */}
      <div style={{
        position: 'absolute',
        right: 0, top: 0, bottom: 0,
        display: 'flex',
        zIndex: 0,
      }}>
        {actions.map((action, i) => (
          <button
            key={i}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              snapClose();
              action.onAction();
            }}
            style={{
              width: `${ACTION_BTN_WIDTH}px`,
              border: 'none',
              background: action.color || '#888',
              color: '#fff',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '3px',
              fontSize: '0.72rem',
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            <span style={{ fontSize: '1.3rem', lineHeight: 1 }}>{action.icon}</span>
            <span>{action.label}</span>
          </button>
        ))}
      </div>

      {/* Sliding content */}
      <div
        ref={contentRef}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        style={{
          transform: 'translateX(0)',
          position: 'relative',
          zIndex: 1,
          willChange: 'transform',
          background: 'var(--bg-card, #fff)',
          boxShadow: '3px 0 0 3px var(--bg-card, #fff)',
        }}
      >
        {children}
      </div>
    </div>
  );
}
